import { GameIndicator, GameModel, GameState } from '@/models/GameModel';
import { GameAdvancedSettingsModel } from '@/models/settings/GameAdvancedSettingsModel';
import {
  BackgroundBehaviorType,
  BackgroundPositionType,
  GameQuizData,
  GameQuizGeneralData,
  GameQuizQuestionData
} from '@/components/games/quiz/Data';
import { AlignContentType, BehaviorTypes, PositionTypes } from '@/typings/enums/enums';
import { AnswerLayoutSettingsModel } from '@/models/settings/AnswerLayoutSettingsModel';
import { VisibilityConditionsModel } from '@/models/conditions/VisibilityConditionsModel';
import { GameEndingConditionType } from '@/typings/interfaces/data/conditions/visibilityConditions';
import { GameSectionImageOverlay } from '@/store/modules/global/state';
import { GameIndicatorIcon } from '@/components/indicators/Model';
import useDevice from '@/hooks/useDevice';
import { formatUrl } from '@/utilities/Url';

interface VisibilityConditionData {
  condition: GameEndingConditionType;
  date_range: {
    enabled: string;
    date_to: string;
    date_from: string;
  };
}

export enum QuizGameTypes {
  STANDARD,
  RANDOMIZE
}

export enum QuizVideoPositionTypes {
  ABOVE,
  LEFT,
  RIGHT
}

export enum QuizAnswerTypes {
  TEXT,
  IMAGE,
  SLIDER,
  SWIPE
}

export interface GameQuizQuestionFeedbackState {
  enabled: boolean;
  redirectToNextQuestion?: boolean;
  delayUntilNext?: number;
  buttonNextLabel?: string;
  previousQuestionLabel?: string;
  feedbackButtonLabel?: string;
  buttonLastLabel?: string;
  answerMessage?: {
    correct?: string;
    wrong?: string;
  };
}

export interface CustomLabelState {
  buttons: {
    answer?: string;
    lastAnswer?: string;
    selectAnswer?: string;
  };
  linkLabel?: string;
}

export interface GameQuizGeneralState {
  gameType: QuizGameTypes;
  contentAlign: AlignContentType;
  answerAlignment: AlignContentType;
  questionsShown?: number;
  previousQuestionLabel?: string;
  correctAnswersRequired?: number;
  loserOnIncorrectAnswer: boolean;
  feedback: GameQuizQuestionFeedbackState;
  showAnswerButton: boolean;
  enableSkipLogic: boolean;
  showCorrectAnswer: boolean;
  customLabels: CustomLabelState;
  time: {
    enabled: boolean;
    limit?: number;
    perQuestion: boolean;
  };
  features: {
    bestTime: boolean;
    timeRanking: boolean;
  };
  goBackwards: {
    enabled: boolean;
  };
  activeStateColor?: string;
  activeStateBackgroundColor?: string;
  backgroundImageEnabled?: boolean;
}

export interface GameQuizAnswerActionState {
  action?: string | number;
  actionPage?: number;
}

export interface GameQuizAnswerState extends GameQuizAnswerActionState {
  id: number;
  text: string;
  description?: string;
  percentage?: number;
}

export interface GameQuizQuestionIntroductionState {
  enabled: boolean;
  content?: string;
  buttonLabel?: string;
}

export interface GameQuizQuestionAnswerMessageState {
  correct?: string;
  wrong?: string;
}

export interface GameQuizSectionImageOverlay extends GameSectionImageOverlay {
  onFeedback: boolean;
}

export interface GameQuizQuestionState {
  id: number;
  question: string;
  swipeImage?: string;
  video: {
    enabled: boolean;
    url?: string;
    position?: QuizVideoPositionTypes;
    width?: number;
  };
  sliderRotation?: {
    enabled: boolean;
    onlyMobile?: boolean;
  };
  questionKey: number;
  introduction: GameQuizQuestionIntroductionState;
  backgroundImage: GameQuizSectionImageOverlay;
  questionImage?: string;
  questionButtonLabel: string;
  previousQuestionLabel?: string;
  feedbackButtonLabel: string;
  enableMultipleAnswers: boolean;
  minAnswers?: number;
  maxAnswers?: number;
  minAnswersValidationMessage?: string;
  maxAnswersValidationMessage?: string;
  enableDescription: boolean;
  answerType: QuizAnswerTypes;
  answers: GameQuizAnswerState[];
  multipleCorrectAnswers: boolean;
  correctAnswers: number[];
  link: {
    enabled: boolean;
    url?: string;
  };
  date: {
    enabled: boolean;
    from?: string;
    to?: string;
  };
  visibilityCondition?: VisibilityConditionsModel;
  answerMessage: GameQuizQuestionAnswerMessageState;
  feedback: GameQuizQuestionFeedbackState;
  questionString?: string;
  correctString?: string;
  wrongString?: string;
  enablePeopleAnswers: boolean;
  answerLayout: AnswerLayoutSettingsModel;
  answerColor?: {
    correct?: string;
    wrong?: string;
  };
  defaultAnswer?: number;
}

export interface GameQuizTranslationState {
  swipe?: {
    left?: string;
    right?: string;
  };
}

export interface GameQuizState extends GameState {
  general: GameQuizGeneralState;
  questions: GameQuizQuestionState[];
  advanced?: GameAdvancedSettingsModel;
  translation?: GameQuizTranslationState;
}

export class GameQuizModel extends GameModel<GameQuizData, GameQuizState> {
  parseGame(data: GameQuizData) {
    const state = this.state;
    state.general = GameQuizModel.constructGeneralState(data.general);

    const questions: GameQuizQuestionState[] = [];

    for (const property in data.questions.question) {
      if (Object.hasOwnProperty.call(data.questions.question, property)) {
        questions.push(
          this.constructQuestionState(data.questions.question[Number(property)], data.general, Number(property))
        );
      }
    }

    state.questions = questions;

    /**
     * To support legacy formats/copied campaigns
     * Issue can be customer has copied a campaign where required correct answers cna be higher than
     * available question. This happens if a customer has copied a campaign with a lot of questions
     * Then removed questions, but haven't set the option for requred correct answers to the the length
     * of available questions
     */
    if (state.general.correctAnswersRequired) {
      if (state.general.correctAnswersRequired > state.questions.length) {
        state.general.correctAnswersRequired = state.questions.length;
      }
    }

    if (data.advanced) {
      if (state.advanced) {
        state.advanced.setData(data.advanced);
      } else {
        state.advanced = new GameAdvancedSettingsModel(data.advanced);
      }
    } else {
      state.advanced = undefined;
    }

    if (data.translation) {
      state.translation = {
        swipe: {
          right: data.translation.right_swipe,
          left: data.translation.left_swipe
        }
      };
    } else {
      state.translation = undefined;
    }
  }

  private static constructGeneralState(data: GameQuizGeneralData): GameQuizGeneralState {
    let showCorrectAnswer = false;
    if (typeof data.show_correct_answer === 'number') {
      showCorrectAnswer = data.show_correct_answer === 1;
    } else {
      showCorrectAnswer = data?.show_correct_answer ? data?.show_correct_answer : false;
    }
    return {
      gameType: data.quiz_type === 'randomize' ? QuizGameTypes.RANDOMIZE : QuizGameTypes.STANDARD,
      contentAlign: data.quiz_align ?? AlignContentType.CENTER,
      ...(data.previous_question && { previousQuestionLabel: data.previous_question }),
      answerAlignment: data.answer_alignment ?? AlignContentType.CENTER,
      enableSkipLogic: data.conditional ? data.conditional === '1' : false,
      ...(data?.questions_shown && { questionsShown: Number(data.questions_shown) }),
      ...(data?.correct_answers && { correctAnswersRequired: Number(data.correct_answers) }),
      loserOnIncorrectAnswer: data?.loser_on_incorrect_answer === '1',
      feedback: {
        enabled: data.show_answer === '1',
        ...(data.show_answer &&
          data.show_answer === '1' && {
            redirectToNextQuestion: data.show_answer_redirect === '1',
            delayUntilNext: data.show_answer_delay ? Number(data.show_answer_delay) : undefined,
            buttonNextLabel: data.next_question,
            answerMessage: {
              correct: GameQuizModel.feedbackMessagePrioritize(data.correct_answer_message ?? ''),
              wrong: GameQuizModel.feedbackMessagePrioritize(data.wrong_answer_message ?? '')
            }
          })
      },
      showAnswerButton: data.show_answer_button ? data.show_answer_button === '1' : true,
      showCorrectAnswer: showCorrectAnswer,
      customLabels: {
        buttons: {
          answer: data.answer_btn,
          lastAnswer: data.answer_btn_last,
          selectAnswer: data.select_answer_btn ? data.select_answer_btn : 'Pick an answer'
        },
        linkLabel: data?.link
      },
      features: {
        bestTime: data.enable_best_time === '1',
        timeRanking: data.enable_time_ranking === '1'
      },
      time: {
        enabled: data.time === 1,
        ...(data?.time_limit && { limit: Number(data.time_limit) }),
        perQuestion: data.time_per_question === '1'
      },
      goBackwards: {
        enabled: data.go_backwards?.enabled
          ? data.go_backwards?.enabled === '1' ||
            data.go_backwards?.enabled === 1 ||
            data.go_backwards?.enabled === true
          : false
      },
      activeStateColor: data.active_state_color,
      activeStateBackgroundColor: data.active_state_bg_color
    };
  }

  private constructQuestionState(
    data: GameQuizQuestionData,
    generalData: GameQuizGeneralData,
    index: number
  ): GameQuizQuestionState {
    let questionVideoPosition: QuizVideoPositionTypes = QuizVideoPositionTypes.ABOVE;

    if (data.question_video && data.question_video.position) {
      switch (data.question_video.position) {
        case 'left':
          questionVideoPosition = QuizVideoPositionTypes.LEFT;
          break;

        case 'right':
          questionVideoPosition = QuizVideoPositionTypes.RIGHT;
          break;

        default:
          questionVideoPosition = QuizVideoPositionTypes.ABOVE;
          break;
      }
    }

    let behaviourType: BehaviorTypes = BehaviorTypes.COVER;
    let positionType: PositionTypes = PositionTypes.CENTER_CENTER;

    switch (data.background_behavior) {
      case 'center':
        behaviourType = BehaviorTypes.CENTER;
        break;
      case 'cover':
        behaviourType = BehaviorTypes.COVER;
        break;
      case 'contain':
        behaviourType = BehaviorTypes.CONTAIN;
        break;
      case 'stretch':
        behaviourType = BehaviorTypes.STRETCH;
        break;
      case 'tile':
        behaviourType = BehaviorTypes.TILE;
        break;
      case 'actual_size':
        behaviourType = BehaviorTypes.ACTUAL_SIZE;
        break;
    }

    switch (data.background_position) {
      case 'custom':
        positionType = PositionTypes.CUSTOM;
        break;
      case 'bottom_center':
        positionType = PositionTypes.BOTTOM_CENTER;
        break;
      case 'bottom_left':
        positionType = PositionTypes.BOTTOM_LEFT;
        break;
      case 'bottom_right':
        positionType = PositionTypes.BOTTOM_RIGHT;
        break;
      case 'center_center':
        positionType = PositionTypes.CENTER_CENTER;
        break;
      case 'center_left':
        positionType = PositionTypes.CENTER_LEFT;
        break;
      case 'center_right':
        positionType = PositionTypes.CENTER_RIGHT;
        break;
      case 'top_center':
        positionType = PositionTypes.TOP_CENTER;
        break;
      case 'top_left':
        positionType = PositionTypes.TOP_LEFT;
        break;
      case 'top_right':
        positionType = PositionTypes.TOP_RIGHT;
        break;
    }

    const existingQuestionState = this.state?.questions?.find(
      (existingQuestion) => existingQuestion.id === Number(data.id)
    );

    let visibilityCondition: VisibilityConditionsModel | undefined;

    if (data.date_range.enabled === '1') {
      visibilityCondition = existingQuestionState ? existingQuestionState.visibilityCondition : undefined;

      const visibilityConditionData: VisibilityConditionData = {
        condition: GameEndingConditionType.NONE,
        date_range: {
          enabled: data.date_range.enabled,
          date_to: data.date_range.to ? data.date_range.to : '',
          date_from: data.date_range.from ? data.date_range.from : ''
        }
      };

      if (visibilityCondition) {
        visibilityCondition.setData(visibilityConditionData);
      } else {
        visibilityCondition = new VisibilityConditionsModel(visibilityConditionData);
      }
    }

    let answerLayout: AnswerLayoutSettingsModel | undefined;
    answerLayout = existingQuestionState ? existingQuestionState.answerLayout : undefined;
    if (answerLayout) {
      answerLayout.setData(generalData);
    } else {
      answerLayout = new AnswerLayoutSettingsModel(generalData);
    }

    let correctAnswers: number[] = [];

    if (data.correct_answer instanceof Array) {
      correctAnswers = data.correct_answer.map(function (x) {
        return parseInt(x.replace(/^\D+/g, ''), 10);
      });
    } else if (typeof data.correct_answer === 'string') {
      correctAnswers.push(parseInt(data.correct_answer.replace(/^\D+/g, ''), 10));
    }

    /**
     * only assign mobile values if overwrite mobile is false else ignore mobile values and assign default object.
     * This functionality is much like the check we have in getDeviceData, however
     * we have pulled this functionality in locally in this model as the data in this
     * is formatted differently, so we couldn't make a 100% use of getDeviceData
     */

    let backgroundImage: GameQuizSectionImageOverlay = {
      enabled: !!data.background_image && generalData.background_image?.enabled === true,
      src: data.background_image,
      behavior: behaviourType,
      position: positionType,
      overlay: data.background_overlay,
      onFeedback: data.background_feedback_enabled === '1'
    };

    const { isMobile } = useDevice();
    const overwriteMobile = typeof data['overwrite_mobile'] === 'undefined' || data['overwrite_mobile'] === '1';
    if (isMobile && !overwriteMobile) {
      backgroundImage = {
        enabled: !!data.mobile.background_image,
        src: data.mobile.background_image,
        behavior: this.getBackgroundBehavior(data.mobile.background_behavior),
        position: this.getBackgroundPosition(data.mobile.background_position),
        overlay: data.mobile.background_overlay,
        onFeedback: data.mobile.background_feedback_enabled === '1'
      };
    }

    const questionState: GameQuizQuestionState = {
      id: Number(data.id),
      questionKey: Number(data.question_key ? data.question_key : index),
      question: data.question,
      video: {
        enabled: data.question_video ? data.question_video.enable === '1' : false,
        ...(data.question_video &&
          data.question_video.enable &&
          data.question_video.enable === '1' && {
            url: data.question_video.url,
            width: data.question_video.width ? Number(data.question_video.width) : undefined,
            position: questionVideoPosition
          })
      },
      introduction: {
        enabled: data.introduction?.enabled === '1',
        content: data.introduction?.content,
        buttonLabel: data.introduction?.button_label ? data.introduction?.button_label : generalData.next_question
      },
      backgroundImage: backgroundImage,
      enableMultipleAnswers: data.multiple_answers === '1',
      ...(data.multiple_answers && {
        ...(data.max_answers && { maxAnswers: Number(data.max_answers) }),
        ...(data.min_answers && { minAnswers: Number(data.min_answers) }),
        ...(data.min_answers_validation_message && {
          minAnswersValidationMessage: data.min_answers_validation_message
        }),
        ...(data.max_answers_validation_message && { maxAnswersValidationMessage: data.max_answers_validation_message })
      }),
      sliderRotation: {
        enabled: data.slider_rotation === '1',
        ...(data.slider_rotation_mobile && { onlyMobile: data.slider_rotation_mobile === '1' })
      },
      questionImage: data.question_image,
      ...(data.swipe_image && { swipeImage: data.swipe_image }),
      enableDescription: data.additional_description === '1',
      answers: GameQuizModel.constructAnswerState(data),
      multipleCorrectAnswers: data.multiple_correct_answers === '1',
      correctAnswers: correctAnswers,
      questionButtonLabel: data.question_button_label,
      feedbackButtonLabel: data.feedback_button_label,
      ...(generalData.previous_question && { previousQuestionLabel: generalData.previous_question }),
      date: {
        enabled: data.date_range.enabled === '1',
        ...(data.date_range.enabled &&
          data.date_range.enabled === '1' && {
            from: data.date_range.from,
            to: data.date_range.to
          })
      },
      visibilityCondition,
      link: {
        enabled: !!data.link,
        ...(!!data.link && {
          // eslint-disable-next-line security/detect-non-literal-fs-filename
          url: formatUrl(data.link)
        })
      },
      answerMessage: {
        correct: data.correct_answer_message ? data.correct_answer_message : data.correct_string,
        wrong: data.wrong_answer_message ? data.wrong_answer_message : data.wrong_string
      },

      feedback: {
        enabled: generalData.show_answer === '1',
        ...(generalData.show_answer &&
          generalData.show_answer === '1' && {
            redirectToNextQuestion: generalData.show_answer_redirect === '1',
            delayUntilNext: generalData.show_answer_delay ? Number(generalData.show_answer_delay) : undefined,
            buttonNextLabel: generalData.next_question,
            feedbackButtonLabel: data.feedback_button_label,
            buttonLastLabel: generalData.answer_btn_last,
            answerMessage: {
              correct: GameQuizModel.feedbackMessagePrioritize(
                data.correct_answer_message ?? '',
                generalData.correct_answer_message ?? ''
              ),
              wrong: GameQuizModel.feedbackMessagePrioritize(
                data.wrong_answer_message ?? '',
                generalData.wrong_answer_message ?? ''
              )
            }
          })
      },
      questionString: data.question_string,
      correctString: data.correct_string,
      wrongString: data.wrong_string,
      answerLayout,
      enablePeopleAnswers: !!generalData.other_peoples_answer,
      answerColor: {
        correct: generalData.correct_answer_color ? generalData.correct_answer_color : '#68bb5a',
        wrong: generalData.wrong_answer_color ? generalData.wrong_answer_color : '#bb5a80'
      },
      defaultAnswer: data.default_answer ? Number(data.default_answer) : undefined,
      answerType: QuizAnswerTypes.TEXT
    };

    switch (data.answer_type) {
      case 'image':
        questionState.answerType = QuizAnswerTypes.IMAGE;
        break;

      case 'slider':
        questionState.answerType = QuizAnswerTypes.SLIDER;
        break;

      case 'swipe':
        questionState.answerType = QuizAnswerTypes.SWIPE;
        break;

      case 'text':
        questionState.answerType = QuizAnswerTypes.TEXT;
        break;
    }

    return questionState;
  }

  private static feedbackMessagePrioritize(...messages: string[]): string | undefined {
    return messages.find((message) => {
      return (
        message &&
        message.trim() &&
        [
          '<div><br></div>',
          '<br>',
          '<p><br></p>',
          '<h1><br></h1>',
          '<h2><br></h2>',
          '<h3><br></h3>',
          '<h4><br></h4>',
          '<h5><br></h5>',
          '<h6><br></h6>',
          '<div style="text-align: center;"><br></div>',
          '<p style="text-align: center;"><br></p>',
          '<h1 style="text-align: center;"><br></h1>',
          '<h2 style="text-align: center;"><br></h2>',
          '<h3 style="text-align: center;"><br></h3>',
          '<h4 style="text-align: center;"><br></h4>',
          '<h5 style="text-align: center;"><br></h5>',
          '<h6 style="text-align: center;"><br></h6>'
        ].indexOf(message.replace(/&#60;/gi, '<').replace(/&#62;/gi, '>')) === -1
      );
    });
  }

  private static constructAnswerState(data: GameQuizQuestionData): GameQuizAnswerState[] {
    const answers: GameQuizAnswerState[] = [];
    if (data.answer_1) {
      answers.push({
        id: 1,
        text: data.answer_1,
        ...(data?.answer_1_additional && { description: data.answer_1_additional }),
        ...(Number.isInteger(data?.answer_1_percentage) && { percentage: data.answer_1_percentage }),
        ...(data?.answer_1_action && { action: data.answer_1_action }),
        ...(data?.answer_1_action_page && { actionPage: Number(data.answer_1_action_page) })
      });
    }

    if (data.answer_2) {
      answers.push({
        id: 2,
        text: data.answer_2,
        ...(data?.answer_2_additional && { description: data.answer_2_additional }),
        ...(Number.isInteger(data?.answer_2_percentage) && { percentage: data.answer_2_percentage }),
        ...(data?.answer_2_action && { action: data.answer_2_action }),
        ...(data?.answer_2_action_page && { actionPage: Number(data.answer_2_action_page) })
      });
    }

    if (data.answer_3) {
      answers.push({
        id: 3,
        text: data.answer_3,
        ...(data?.answer_3_additional && { description: data.answer_3_additional }),
        ...(Number.isInteger(data?.answer_3_percentage) && { percentage: data.answer_3_percentage }),
        ...(data?.answer_3_action && { action: data.answer_3_action }),
        ...(data?.answer_3_action_page && { actionPage: Number(data.answer_3_action_page) })
      });
    }

    if (data.answer_4) {
      answers.push({
        id: 4,
        text: data.answer_4,
        ...(data?.answer_4_additional && { description: data.answer_4_additional }),
        ...(Number.isInteger(data?.answer_4_percentage) && { percentage: data.answer_4_percentage }),
        ...(data?.answer_4_action && { action: data.answer_4_action }),
        ...(data?.answer_4_action_page && { actionPage: Number(data.answer_4_action_page) })
      });
    }

    if (data.answer_5) {
      answers.push({
        id: 5,
        text: data.answer_5,
        ...(data?.answer_5_additional && { description: data.answer_5_additional }),
        ...(Number.isInteger(data?.answer_5_percentage) && { percentage: data.answer_5_percentage }),
        ...(data?.answer_5_action && { action: data.answer_5_action }),
        ...(data?.answer_5_action_page && { actionPage: Number(data.answer_5_action_page) })
      });
    }

    if (data.answer_6) {
      answers.push({
        id: 6,
        text: data.answer_6,
        ...(data?.answer_6_additional && { description: data.answer_6_additional }),
        ...(Number.isInteger(data?.answer_6_percentage) && { percentage: data.answer_6_percentage }),
        ...(data?.answer_6_action && { action: data.answer_6_action }),
        ...(data?.answer_6_action_page && { actionPage: Number(data.answer_6_action_page) })
      });
    }

    if (data.answer_7) {
      answers.push({
        id: 7,
        text: data.answer_7,
        ...(data?.answer_7_additional && { description: data.answer_7_additional }),
        ...(Number.isInteger(data?.answer_7_percentage) && { percentage: data.answer_7_percentage }),
        ...(data?.answer_7_action && { action: data.answer_7_action }),
        ...(data?.answer_7_action_page && { actionPage: Number(data.answer_7_action_page) })
      });
    }

    if (data.answer_8) {
      answers.push({
        id: 8,
        text: data.answer_8,
        ...(data?.answer_8_additional && { description: data.answer_8_additional }),
        ...(Number.isInteger(data?.answer_8_percentage) && { percentage: data.answer_8_percentage }),
        ...(data?.answer_8_action && { action: data.answer_8_action }),
        ...(data?.answer_8_action_page && { actionPage: Number(data.answer_8_action_page) })
      });
    }

    if (data.answer_9) {
      answers.push({
        id: 9,
        text: data.answer_9,
        ...(data?.answer_9_additional && { description: data.answer_9_additional }),
        ...(Number.isInteger(data?.answer_9_percentage) && { percentage: data.answer_9_percentage }),
        ...(data?.answer_9_action && { action: data.answer_9_action }),
        ...(data?.answer_9_action_page && { actionPage: Number(data.answer_9_action_page) })
      });
    }

    if (data.answer_10) {
      answers.push({
        id: 10,
        text: data.answer_10,
        ...(data?.answer_10_additional && { description: data.answer_10_additional }),
        ...(Number.isInteger(data?.answer_10_percentage) && { percentage: data.answer_10_percentage }),
        ...(data?.answer_10_action && { action: data.answer_10_action }),
        ...(data?.answer_10_action_page && { actionPage: Number(data.answer_10_action_page) })
      });
    }

    if (data.answer_11) {
      answers.push({
        id: 11,
        text: data.answer_11,
        ...(data?.answer_11_additional && { description: data.answer_11_additional }),
        ...(Number.isInteger(data?.answer_11_percentage) && { percentage: data.answer_11_percentage }),
        ...(data?.answer_11_action && { action: data.answer_11_action }),
        ...(data?.answer_11_action_page && { actionPage: Number(data.answer_11_action_page) })
      });
    }

    if (data.answer_12) {
      answers.push({
        id: 12,
        text: data.answer_12,
        ...(data?.answer_12_additional && { description: data.answer_12_additional }),
        ...(Number.isInteger(data?.answer_12_percentage) && { percentage: data.answer_12_percentage }),
        ...(data?.answer_12_action && { action: data.answer_12_action }),
        ...(data?.answer_12_action_page && { actionPage: Number(data.answer_12_action_page) })
      });
    }

    if (data.answer_13) {
      answers.push({
        id: 13,
        text: data.answer_13,
        ...(data?.answer_13_additional && { description: data.answer_13_additional }),
        ...(Number.isInteger(data?.answer_13_percentage) && { percentage: data.answer_13_percentage }),
        ...(data?.answer_13_action && { action: data.answer_13_action }),
        ...(data?.answer_13_action_page && { actionPage: Number(data.answer_13_action_page) })
      });
    }

    if (data.answer_14) {
      answers.push({
        id: 14,
        text: data.answer_14,
        ...(data?.answer_14_additional && { description: data.answer_14_additional }),
        ...(Number.isInteger(data?.answer_14_percentage) && { percentage: data.answer_14_percentage }),
        ...(data?.answer_14_action && { action: data.answer_14_action }),
        ...(data?.answer_14_action_page && { actionPage: Number(data.answer_14_action_page) })
      });
    }

    if (data.answer_15) {
      answers.push({
        id: 15,
        text: data.answer_15,
        ...(data?.answer_15_additional && { description: data.answer_15_additional }),
        ...(Number.isInteger(data?.answer_15_percentage) && { percentage: data.answer_15_percentage }),
        ...(data?.answer_15_action && { action: data.answer_15_action }),
        ...(data?.answer_15_action_page && { actionPage: Number(data.answer_15_action_page) })
      });
    }

    if (data.answer_16) {
      answers.push({
        id: 16,
        text: data.answer_16,
        ...(data?.answer_16_additional && { description: data.answer_16_additional }),
        ...(Number.isInteger(data?.answer_16_percentage) && { percentage: data.answer_16_percentage }),
        ...(data?.answer_16_action && { action: data.answer_16_action }),
        ...(data?.answer_16_action_page && { actionPage: Number(data.answer_16_action_page) })
      });
    }

    return answers;
  }

  public getIndicators(): GameIndicator[] {
    return [
      {
        indicatorKey: 'time',
        metricKey: {
          time_left: 'timeleft',
          time_used: 'timeused'
        },
        icon: GameIndicatorIcon.TIME,
        value: {
          time_left: this.state.general.time.limit ?? 0,
          time_used: 1
        }
      }
    ];
  }

  private getBackgroundPosition(data?: BackgroundPositionType) {
    let positionType: PositionTypes | undefined;

    switch (data) {
      case 'custom':
        positionType = PositionTypes.CUSTOM;
        break;
      case 'bottom_center':
        positionType = PositionTypes.BOTTOM_CENTER;
        break;
      case 'bottom_left':
        positionType = PositionTypes.BOTTOM_LEFT;
        break;
      case 'bottom_right':
        positionType = PositionTypes.BOTTOM_RIGHT;
        break;
      case 'center_left':
        positionType = PositionTypes.CENTER_LEFT;
        break;
      case 'center_right':
        positionType = PositionTypes.CENTER_RIGHT;
        break;
      case 'top_center':
        positionType = PositionTypes.TOP_CENTER;
        break;
      case 'top_left':
        positionType = PositionTypes.TOP_LEFT;
        break;
      case 'top_right':
        positionType = PositionTypes.TOP_RIGHT;
        break;
      default:
        positionType = PositionTypes.CENTER_CENTER;
        break;
    }
    return positionType;
  }
  private getBackgroundBehavior(data?: BackgroundBehaviorType) {
    let behaviourType: BehaviorTypes | undefined;

    switch (data) {
      case 'center':
        behaviourType = BehaviorTypes.CENTER;
        break;

      case 'contain':
        behaviourType = BehaviorTypes.CONTAIN;
        break;
      case 'stretch':
        behaviourType = BehaviorTypes.STRETCH;
        break;
      case 'tile':
        behaviourType = BehaviorTypes.TILE;
        break;
      case 'actual_size':
        behaviourType = BehaviorTypes.ACTUAL_SIZE;
        break;
      default:
        behaviourType = BehaviorTypes.COVER;
        break;
    }
    return behaviourType;
  }

  public isGameValid(): boolean {
    // is valid logic here
    // const data = this.getData();
    return true;
  }

  public authorSignature() {
    return 'Ani Mikova';
  }
}
